import { useCallback, useEffect, useId, useMemo, useRef, useState, type RefObject } from "react";
import { useColumnFilter } from "@/hooks/use-column-filter";
import { ColumnFilterPopover } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";
import {
    Button,
    ButtonUtility,
    Input,
    ModalOverlay,
    Modal,
    Dialog,
    RadioButton,
    RadioGroup,
    Select,
    Toggle,
} from "@its-engineering-and-development/ui-foundation/components";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import {
    useGroupsList,
    useGroupDetail,
    useUpdateGroup,
    useCreateGroup,
    useApproverAndAccountantRoleIds,
    useGroupRoleUsers,
    useGroupUsersSearch,
    useAssignGroupMembers,
    useGroupMembers,
    useGroupsUsersByBatch,
    useRemoveGroupMember,
    GroupConcurrencyError,
} from "@app/hooks/use-groups";
import type { ApproverUser, GroupsQueryFilters, GroupMembersQueryFilters, GroupMembersSortBy } from "@app/hooks/use-groups";
import { USER_SORT_FIELD, USER_FILTER_FIELD } from "@app/constants/user-sort-fields";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { ACTIONS } from "@/auth";
import { useCan } from "@/hooks/use-can";
import { useGetPoliciesForGroup, parseReceiptRequiredAmount } from "@app/hooks/use-policy";
import { DataTableShell } from "@app/components/base/data-table/data-table";
import { Drawer } from "@app/components/base/drawer/drawer";
import { Spinner } from "@app/components/base/spinner/spinner";
import { useInfiniteScrollOnVisible } from "@app/hooks/use-infinite-scroll-on-visible";
import type { PolicyForGroup } from "@app/hooks/use-policy";
import { GroupApproverMode } from "@app/types/groups";
import type { GroupItem, GroupDetailItem, GroupApproverUser, GroupMemberUser } from "@app/types/groups";
import { formatDate } from "@/utils/date";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const RECEIPT_POLICY_NONE_KEY = "__none__";

// Placeholder passed to GroupDetailContent while creating a brand-new group — there's no
// fetched GroupDetailItem yet, so the read-only display fields (status, members, custom
// fields, notify email) fall back to sensible create-mode defaults.
const EMPTY_GROUP_FOR_CREATE: GroupDetailItem = {
    groupId: "",
    groupName: "",
    isActive: true,
    memberCount: 0,
    createdAt: null,
    updatedAt: null,
    updatedBy: null,
    policyExceptions: [],
    policyIds: [],
    description: null,
    companyId: null,
    isDefault: false,
    createdBy: null,
    members: [],
    approverConfig: null,
    notifyEmail: null,
    customFields: [],
    groupEpoch: null,
    accountants: [],
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconDownload() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 3v12m0 0-4-4m4 4 4-4M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
        </svg>
    );
}

function IconGroupPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M17 20c0-2.21-2.24-4-5-4s-5 1.79-5 4M12 12a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
            <path strokeLinecap="round" d="M19 8v6m-3-3h6" />
        </svg>
    );
}

// ─── Column filter options ────────────────────────────────────────────────────

const GROUP_STATUS_FILTER_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

function GroupStatusBadge({ isActive }: { isActive: boolean }) {
    return (
        <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
            }`}
        >
            {isActive ? "Active" : "Inactive"}
        </span>
    );
}

// ─── Groups Table ────────────────────────────────────────────────────────────────────

type GroupColumnKey = "groupName" | "status" | "members" | "exceptions" | "created" | "lastUpdate" | "editedBy";

const GROUPS_SEARCH_HISTORY_KEY = "expense-admin:groups-column-search-history";

function GroupsTable({
    groups,
    isLoading,
    pageInfo,
    onPageChange,
    onSelect,
    filter,
    onFilter,
}: {
    groups: GroupItem[];
    isLoading: boolean;
    pageInfo: TablePageInfo | null;
    onPageChange: (page: number) => void;
    onSelect: (groupId: string) => void;
    filter: GroupsQueryFilters;
    onFilter: (f: GroupsQueryFilters) => void;
}) {
    const { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved, applyFilter } =
        useColumnFilter<GroupColumnKey>(GROUPS_SEARCH_HISTORY_KEY);

    const sortByMap: Partial<Record<GroupColumnKey, GroupsQueryFilters["sortBy"]>> = {
        groupName: "name",
        status: "status",
        created: "createdAt",
        lastUpdate: "updatedAt",
    };

    const filterFieldMap: Partial<Record<GroupColumnKey, "name" | "status">> = {
        groupName: "name",
        status: "status",
    };

    const handleSort = (key: GroupColumnKey) => {
        const sortBy = sortByMap[key];
        if (!sortBy) return;
        const nextOrder = filter.sortBy === sortBy && filter.sortOrder === "asc" ? "desc" : "asc";
        onFilter({ ...filter, sortBy, sortOrder: nextOrder });
    };

    const applyColumnFilter = (key: GroupColumnKey, value?: string, saveToHistory = true) => {
        const field = filterFieldMap[key];
        if (!field) return;
        const v = (value ?? filterInput).trim();
        if (!v) return;
        applyFilter(v, saveToHistory);
        onFilter({ ...filter, [field]: v });
    };

    const clearColumnFilter = (key: GroupColumnKey) => {
        const field = filterFieldMap[key];
        if (!field) return;
        onFilter({ ...filter, [field]: undefined });
        closeFilter();
    };

    const colHeader = (
        key: GroupColumnKey,
        label: string,
        opts?: { options?: Array<{ label: string; value: string }>; hint?: string; filterable?: boolean; sortable?: boolean },
    ) => {
        const field = filterFieldMap[key];
        const isFilterActive = field ? !!filter[field] : false;
        const sortBy = sortByMap[key];
        const isSortActive = !!sortBy && filter.sortBy === sortBy;
        const sortDir = isSortActive ? filter.sortOrder : undefined;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable={Boolean(opts?.filterable)}
                isFilterActive={isFilterActive}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl)}
                isSortable={Boolean(opts?.sortable)}
                showSortControl
                isSortActive={isSortActive}
                sortDirection={sortDir}
                onSort={() => handleSort(key)}
            />,
            opts?.filterable ? (
                <ColumnFilterPopover
                    key={`${key}-filter`}
                    isOpen={activeFilterColumn === key}
                    anchorEl={activeFilterAnchor}
                    columnLabel={label}
                    hint={opts.hint}
                    options={opts.options}
                    searchValue={filterInput}
                    savedValues={opts.options ? undefined : (searchHistory[key] ?? [])}
                    onSearchChange={setFilterInput}
                    onClose={closeFilter}
                    onApply={(value) => applyColumnFilter(key, value, !opts.options)}
                    onClear={() => clearColumnFilter(key)}
                    onRemoveSavedValue={removeSaved}
                    onSelectValue={opts.options ? undefined : (value) => applyColumnFilter(key, value, false)}
                />
            ) : null,
        ];
    };

    return (
        <DataTableShell
            tabName="groups"
            isLoading={isLoading}
            loadingDescription="Please wait while the groups load."
            minWidth="860px"
            pageInfo={pageInfo}
            onPageChange={onPageChange}
            isEmpty={groups.length === 0}
            hasActiveFilters={Boolean(filter.name || filter.status)}
            emptyMessage={filter.name || filter.status ? "No groups match the current filters." : "No groups found."}
            header={
                <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                    <tr>
                        {colHeader("groupName", "Group name", { filterable: true, sortable: true })}
                        {colHeader("status", "Status", { options: GROUP_STATUS_FILTER_OPTIONS, filterable: true, sortable: true })}
                        {colHeader("members", "Members")}
                        {colHeader("exceptions", "Exceptions")}
                        {colHeader("created", "Created", { sortable: true })}
                        {colHeader("lastUpdate", "Last update", { sortable: true })}
                        {colHeader("editedBy", "Edited by")}
                    </tr>
                </thead>
            }
        >
            <tbody className="divide-y divide-gray-100">
                {groups.map((group) => (
                    <GroupRow key={group.groupId} group={group} onSelect={onSelect} />
                ))}
            </tbody>
        </DataTableShell>
    );
}

// ─── Group Row ────────────────────────────────────────────────────────────────

function GroupRow({ group, onSelect }: { group: GroupItem; onSelect: (groupId: string) => void }) {
    const displayName = group.groupName ?? group.groupId;

    return (
        <tr
            className="cursor-pointer hover:bg-gray-50"
            tabIndex={0}
            data-group-id={group.groupId}
            onClick={() => onSelect(group.groupId)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(group.groupId);
                }
            }}
        >
            {/* Group name */}
            <td className="px-4 py-3 font-medium text-blue-600">{displayName}</td>

            {/* Status */}
            <td className="px-4 py-3">
                <GroupStatusBadge isActive={group.isActive} />
            </td>

            {/* Members */}
            <td className="px-4 py-3 text-gray-700">{group.memberCount}</td>

            {/* Exceptions */}
            <td className="px-4 py-3">
                {group.policyIds.length > 0 ? (
                    <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                        Yes
                    </span>
                ) : (
                    <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500">
                        No
                    </span>
                )}
            </td>

            {/* Created */}
            <td className="px-4 py-3 text-gray-700">{formatDate(group.createdAt)}</td>

            {/* Last update */}
            <td className="px-4 py-3 text-gray-700">{formatDate(group.updatedAt)}</td>

            {/* Edited by */}
            <td className="px-4 py-3 text-gray-700">{group.updatedBy ?? <span className="text-gray-400">—</span>}</td>
        </tr>
    );
}

// ─── Approver Search Dropdown ────────────────────────────────────────────────

function ApproverSearchDropdown({
    roleId,
    isRoleLoading = false,
    selectedUserIds,
    onSelect,
}: {
    roleId: string | null;
    isRoleLoading?: boolean;
    selectedUserIds: string[];
    onSelect: (user: ApproverUser) => void;
}) {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLLIElement>(null);
    const inputId = useId();

    // Debounce so we don't fire a groupRoleUsers query on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { users, isLoading, isLoadingMore, hasMore, loadMore } = useGroupRoleUsers(roleId, debouncedQuery, isOpen);
    const filtered = users.filter((u) => !selectedUserIds.includes(u.userId));
    const isShowingLoading = isLoading || (isRoleLoading && !roleId);

    useInfiniteScrollOnVisible(sentinelRef, {
        hasNextPage: hasMore,
        isLoading: isLoading || isLoadingMore,
        disabled: !isOpen,
        onLoadMore: loadMore,
    });

    useEffect(() => {
        function handleOutside(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
        }
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
                <input
                    id={inputId}
                    type="text"
                    value={query}
                    aria-label="Search by name"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={() => setIsOpen(true)}
                    placeholder="Search by name..."
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
            </div>

            {isOpen && (
                <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md">
                    {isShowingLoading ? (
                        <li className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-400">
                            <Spinner className="h-4 w-4 text-gray-400" />
                            Loading…
                        </li>
                    ) : filtered.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-400">{debouncedQuery ? "No results" : "No users found"}</li>
                    ) : (
                        <>
                            {filtered.map((u) => {
                                const name = [u.legalName?.firstName, u.legalName?.lastName].filter(Boolean).join(" ") || u.emailId || u.userId;
                                return (
                                    <li key={u.userId}>
                                        <button
                                            type="button"
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                onSelect(u);
                                                setQuery("");
                                                setIsOpen(false);
                                            }}
                                        >
                                            <p className="text-sm font-medium text-gray-900">{name}</p>
                                            {u.emailId && <p className="text-xs text-gray-500">{u.emailId}</p>}
                                        </button>
                                    </li>
                                );
                            })}
                            {hasMore && (
                                <li ref={sentinelRef} className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                                    {isLoadingMore && (
                                        <>
                                            <Spinner className="h-3.5 w-3.5 text-gray-400" />
                                            Loading more…
                                        </>
                                    )}
                                </li>
                            )}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}

// ─── Member Search Dropdown ───────────────────────────────────────────────────
// Company-wide "add member" search for the Group members section. Mirrors
// ApproverSearchDropdown but has no roleId (uses groupUsers, not groupRoleUsers)
// and requires at least 3 characters before it searches at all.

function MemberSearchDropdown({ selectedUserIds, onSelect }: { selectedUserIds: string[]; onSelect: (user: GroupApproverUser) => void }) {
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLLIElement>(null);
    const inputId = useId();
    const listboxId = useId();

    // Debounce so we don't fire a groupUsers query on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const { users, isLoading, isLoadingMore, hasMore, loadMore, belowMinChars } = useGroupUsersSearch(debouncedQuery, isOpen);
    const filtered = users.filter((u) => !selectedUserIds.includes(u.userId));

    useInfiniteScrollOnVisible(sentinelRef, {
        hasNextPage: hasMore,
        isLoading: isLoading || isLoadingMore,
        disabled: !isOpen,
        onLoadMore: loadMore,
    });

    useEffect(() => {
        function handleOutside(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
        }
        document.addEventListener("mousedown", handleOutside);
        return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
                <input
                    id={inputId}
                    type="text"
                    value={query}
                    aria-label="Search and add a member"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onClick={() => setIsOpen(true)}
                    placeholder="Search and add a member..."
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
            </div>

            {isOpen && (
                <ul
                    id={listboxId}
                    role="listbox"
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-md"
                >
                    {belowMinChars ? (
                        <li className="px-4 py-3 text-sm text-gray-400">Type at least 3 characters to search</li>
                    ) : isLoading ? (
                        <li className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-400">
                            <Spinner className="h-4 w-4 text-gray-400" />
                            Loading…
                        </li>
                    ) : filtered.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-400">No results</li>
                    ) : (
                        <>
                            {filtered.map((u) => {
                                const name = [u.legalName?.firstName, u.legalName?.lastName].filter(Boolean).join(" ") || u.emailId || u.userId;
                                return (
                                    <li key={u.userId}>
                                        <button
                                            type="button"
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                onSelect(u);
                                                setQuery("");
                                                setIsOpen(false);
                                            }}
                                        >
                                            <p className="text-sm font-medium text-gray-900">{name}</p>
                                            {u.emailId && <p className="text-xs text-gray-500">{u.emailId}</p>}
                                        </button>
                                    </li>
                                );
                            })}
                            {hasMore && (
                                <li ref={sentinelRef} className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                                    {isLoadingMore && (
                                        <>
                                            <Spinner className="h-3.5 w-3.5 text-gray-400" />
                                            Loading more…
                                        </>
                                    )}
                                </li>
                            )}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}

// ─── Policy Search Dropdown ───────────────────────────────────────────────────

function PolicySearchDropdown({
    availablePolicies,
    selectedIds,
    onSelect,
    isLoading,
}: {
    availablePolicies: PolicyForGroup[];
    selectedIds: string[];
    onSelect: (policyId: string) => void;
    isLoading: boolean;
}) {
    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = availablePolicies.filter(
        (policy) => policy.status === "active" && !selectedIds.includes(policy.policyId) && policy.name.toLowerCase().includes(search.toLowerCase()),
    );

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!containerRef.current?.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="11" cy="11" r="8" />
                    <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    aria-label="Search policy exceptions"
                    placeholder="Search policy exceptions..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
            </div>
            {isOpen && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-52 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {isLoading ? (
                        <p className="px-4 py-3 text-sm text-gray-400">Loading policies…</p>
                    ) : filtered.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-400">{search ? "No matching policies" : "No policies available"}</p>
                    ) : (
                        filtered.map((p) => (
                            <button
                                key={p.policyId}
                                type="button"
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                                onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                                onClick={() => {
                                    onSelect(p.policyId);
                                    setSearch("");
                                    setIsOpen(false);
                                    inputRef.current?.blur();
                                }}
                            >
                                {p.name}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
// ─── Skeleton helpers ─────────────────────────────────────────────────────────

function UserPillSkeleton() {
    return <span className="inline-flex h-8 w-32 animate-pulse rounded-lg border border-gray-100 bg-gray-100" />;
}

// ─── Group members table (shared by the preview and the full paginated view) ──

type MemberColumnKey = "name" | "email";

const GROUP_MEMBERS_SEARCH_HISTORY_KEY = "expense-admin:group-members-column-search-history";

function GroupMembersTable({
    users,
    isLoading,
    groupId,
    onRemoved,
    removeGroupMember,
    pendingIds,
    pageInfo,
    onPageChange,
    filter,
    onFilter,
}: {
    users: GroupMemberUser[];
    isLoading: boolean;
    groupId: string;
    onRemoved: (userId: string) => void;
    removeGroupMember: (groupId: string, userId: string) => Promise<void>;
    pendingIds: Set<string>;
    pageInfo?: TablePageInfo | null;
    onPageChange?: (page: number) => void;
    filter?: GroupMembersQueryFilters;
    onFilter?: (f: GroupMembersQueryFilters) => void;
}) {
    const { activeFilterColumn, activeFilterAnchor, filterInput, searchHistory, setFilterInput, openColumnFilter, closeFilter, removeSaved, applyFilter } =
        useColumnFilter<MemberColumnKey>(GROUP_MEMBERS_SEARCH_HISTORY_KEY);

    const filterFieldMap: Record<MemberColumnKey, "name" | "email"> = {
        name: USER_FILTER_FIELD.Name,
        email: USER_FILTER_FIELD.Email,
    };

    // The API's sortBy field names don't match the filter field names 1:1 (email sorts by emailId).
    const sortFieldMap: Record<MemberColumnKey, GroupMembersSortBy> = {
        name: USER_SORT_FIELD.Name,
        email: USER_SORT_FIELD.Email,
    };

    const handleSort = (key: MemberColumnKey) => {
        if (!onFilter || !filter) return;
        const sortBy = sortFieldMap[key];
        const nextOrder = filter.sortBy === sortBy && filter.sortOrder === "asc" ? "desc" : "asc";
        onFilter({ ...filter, sortBy, sortOrder: nextOrder });
    };

    const applyColumnFilter = (key: MemberColumnKey, value?: string, saveToHistory = true) => {
        if (!onFilter || !filter) return;
        const field = filterFieldMap[key];
        const v = (value ?? filterInput).trim();
        if (!v) return;
        applyFilter(v, saveToHistory);
        onFilter({ ...filter, [field]: v });
    };

    const clearColumnFilter = (key: MemberColumnKey) => {
        if (!onFilter || !filter) return;
        const field = filterFieldMap[key];
        onFilter({ ...filter, [field]: undefined });
        closeFilter();
    };

    const colHeader = (key: MemberColumnKey, label: string) => {
        if (!filter || !onFilter) {
            return (
                <th key={key} className="px-4 py-3">
                    {label}
                </th>
            );
        }

        const field = filterFieldMap[key];
        const isFilterActive = !!filter[field];
        const isSortActive = filter.sortBy === sortFieldMap[key];
        const sortDir = isSortActive ? filter.sortOrder : undefined;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable
                isFilterActive={isFilterActive}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl)}
                isSortable
                isSortActive={isSortActive}
                sortDirection={sortDir}
                onSort={() => handleSort(key)}
            />,
            <ColumnFilterPopover
                key={`${key}-filter`}
                isOpen={activeFilterColumn === key}
                anchorEl={activeFilterAnchor}
                columnLabel={label}
                searchValue={filterInput}
                savedValues={searchHistory[key] ?? []}
                onSearchChange={setFilterInput}
                onClose={closeFilter}
                onApply={(value) => applyColumnFilter(key, value)}
                onClear={() => clearColumnFilter(key)}
                onRemoveSavedValue={removeSaved}
                onSelectValue={(value) => applyColumnFilter(key, value, false)}
            />,
        ];
    };

    return (
        <DataTableShell
            tabName="members"
            isLoading={isLoading}
            loadingDescription="Please wait while members load."
            minWidth="0px"
            pageInfo={pageInfo}
            onPageChange={onPageChange}
            isEmpty={users.length === 0}
            hasActiveFilters={Boolean(filter?.name || filter?.email)}
            emptyMessage={filter?.name || filter?.email ? "No members match the current filters." : "No members found."}
            header={
                <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                    <tr>
                        {colHeader("name", "Name")}
                        {colHeader("email", "Email")}
                        <th className="w-14 px-4 py-3" />
                    </tr>
                </thead>
            }
        >
            <tbody className="divide-y divide-gray-100">
                {users.map((u) => {
                    const name = [u.legalName?.firstName, u.legalName?.lastName].filter(Boolean).join(" ") || u.emailId || u.userId;
                    const isPending = pendingIds.has(u.userId);
                    return (
                        <tr key={u.userId} className="hover:bg-gray-50">
                            <td className="px-4 py-1.5 font-medium text-gray-900">{name}</td>
                            <td className="px-4 py-1.5 text-gray-500">{u.emailId ?? "—"}</td>
                            <td className="px-4 py-1.5 text-right">
                                {isPending ? (
                                    <span className="inline-flex h-6 w-6 items-center justify-center">
                                        <svg className="h-3.5 w-3.5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24" aria-label="Removing…">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                        </svg>
                                    </span>
                                ) : (
                                    <ButtonUtility
                                        tooltip="Removing members is temporarily unavailable"
                                        tooltipPlacement="top"
                                        color="tertiary"
                                        size="xs"
                                        isDisabled
                                        aria-label={`Remove ${name}`}
                                        className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                                        icon={
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        }
                                        onClick={() =>
                                            removeGroupMember(groupId, u.userId)
                                                .then(() => onRemoved(u.userId))
                                                .catch(() => undefined)
                                        }
                                    />
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </DataTableShell>
    );
}

// ─── Batch user pill (approver / accountant) ───────────────────────────────────

function BatchUserPill({ user, onRemove }: { user: GroupApproverUser; onRemove: (userId: string) => void }) {
    const displayName = [user.legalName?.firstName, user.legalName?.lastName].filter(Boolean).join(" ") || user.emailId || user.userId;
    return (
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700">
            {displayName}
            <button
                type="button"
                aria-label={`Remove ${displayName}`}
                className="flex items-center justify-center text-gray-400 hover:text-gray-600"
                onClick={() => onRemove(user.userId)}
            >
                ×
            </button>
        </span>
    );
}

// ─── Group Members Full View (paginated, inside the drawer) ───────────────────

const MEMBERS_PAGE_LIMIT = 25;

function GroupMembersFullView({
    groupId,
    totalMembersCount,
    onBack,
    onClose,
    maxWidth,
}: {
    groupId: string;
    totalMembersCount: number;
    onBack: () => void;
    onClose: () => void;
    maxWidth: string;
}) {
    const [filter, setFilter] = useState<GroupMembersQueryFilters>({});
    const { users, pageInfo, currentPage, isLoading, goToPage, reload } = useGroupMembers(groupId, MEMBERS_PAGE_LIMIT, filter);
    const { removeGroupMember, pendingIds } = useRemoveGroupMember();
    const hasActiveFilter = Boolean(filter.name || filter.email || filter.status);

    const handleRemoved = useCallback(() => reload(), [reload]);

    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * MEMBERS_PAGE_LIMIT,
              limit: pageInfo.limit,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    return (
        <>
            <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Group members"
                style={{ maxWidth }}
                className="fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden bg-white shadow-2xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            aria-label="Back to group details"
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            onClick={onBack}
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h2 className="text-xl font-semibold text-gray-900">Group members</h2>
                    </div>
                    <button type="button" aria-label="Close" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" onClick={onClose}>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
                    {pageInfo && (
                        <span className="mb-3 text-sm text-gray-500">
                            {hasActiveFilter ? `${pageInfo.totalCount} of ${totalMembersCount} members` : `${pageInfo.totalCount} members`}
                        </span>
                    )}
                    <GroupMembersTable
                        users={users}
                        isLoading={isLoading}
                        groupId={groupId}
                        onRemoved={handleRemoved}
                        removeGroupMember={removeGroupMember}
                        pendingIds={pendingIds}
                        pageInfo={tablePageInfo}
                        onPageChange={goToPage}
                        filter={filter}
                        onFilter={setFilter}
                    />
                </div>
            </div>
        </>
    );
}

// ─── Group Detail Panel ──────────────────────────────────────────────────────────

interface GroupDetailContentProps {
    group: GroupDetailItem;
    groupId: string;
    groupName: string;
    onGroupNameChange: (value: string) => void;
    isSaving: boolean;
    isCreateMode: boolean;
    selectedPolicyIds: string[];
    onPolicyIdsChange: (ids: string[]) => void;
    availablePolicies: PolicyForGroup[];
    policiesLoading: boolean;
    policiesError: string | null;
    receiptPolicies: PolicyForGroup[];
    receiptPoliciesLoading: boolean;
    receiptPoliciesError: string | null;
    selectedReceiptPolicyId: string | null;
    onReceiptPolicyChange: (id: string | null) => void;
    approverOption: GroupApproverMode | "";
    onApproverOptionChange: (value: GroupApproverMode) => void;
    selectedApprovers: GroupApproverUser[];
    selectedApproverIds: string[];
    onApproverSelect: (user: ApproverUser) => void;
    onApproverRemove: (userId: string) => void;
    isLoadingApproversBatch: boolean;
    isLoadingMoreApprovers: boolean;
    approversBatchHasMore: boolean;
    onLoadMoreApprovers: () => void;
    selectedAccountants: GroupApproverUser[];
    selectedAccountantIds: string[];
    onAccountantSelect: (user: ApproverUser) => void;
    onAccountantRemove: (userId: string) => void;
    isLoadingAccountantsBatch: boolean;
    isLoadingMoreAccountants: boolean;
    accountantsBatchHasMore: boolean;
    onLoadMoreAccountants: () => void;
    onShowAllMembers: () => void;
    membersPreview: GroupMemberUser[];
    membersPreviewLoading: boolean;
    membersTotalCount: number;
    removeGroupMember: (groupId: string, userId: string) => Promise<void>;
    removePendingIds: Set<string>;
    onMemberRemoved: (userId: string) => void;
    selectedNewMembers: GroupApproverUser[];
    onNewMemberSelect: (user: GroupApproverUser) => void;
    onNewMemberRemove: (userId: string) => void;
    existingMemberIds: string[];
}

function GroupDetailContent({
    group,
    groupId,
    groupName,
    onGroupNameChange,
    isSaving,
    isCreateMode,
    selectedPolicyIds,
    onPolicyIdsChange,
    availablePolicies,
    policiesLoading,
    policiesError,
    receiptPolicies,
    receiptPoliciesLoading,
    receiptPoliciesError,
    selectedReceiptPolicyId,
    onReceiptPolicyChange,
    approverOption,
    onApproverOptionChange,
    selectedApprovers,
    selectedApproverIds,
    onApproverSelect,
    onApproverRemove,
    isLoadingApproversBatch,
    isLoadingMoreApprovers,
    approversBatchHasMore,
    onLoadMoreApprovers,
    selectedAccountants,
    selectedAccountantIds,
    onAccountantSelect,
    onAccountantRemove,
    isLoadingAccountantsBatch,
    isLoadingMoreAccountants,
    accountantsBatchHasMore,
    onLoadMoreAccountants,
    onShowAllMembers,
    membersPreview,
    membersPreviewLoading,
    membersTotalCount,
    removeGroupMember,
    removePendingIds,
    onMemberRemoved,
    selectedNewMembers,
    onNewMemberSelect,
    onNewMemberRemove,
    existingMemberIds,
}: GroupDetailContentProps) {
    const { approverRoleId, accountantRoleId, isLoading: isLoadingRoleIds } = useApproverAndAccountantRoleIds();

    const selectedReceiptPolicy = receiptPolicies.find((p) => p.policyId === selectedReceiptPolicyId) ?? null;
    const selectedReceiptPolicyStatus = selectedReceiptPolicy?.status;
    const selectedReceiptPolicyTitle =
        selectedReceiptPolicyStatus === "inactive" ? "Policy Inactive" : selectedReceiptPolicyStatus === "deleted" ? "Policy Deleted" : undefined;

    return (
        <div className="flex flex-col gap-6 px-5 py-5">
            {/* Status */}
            <div>
                <div className="mb-1.5 flex items-center justify-between">
                    <p className="text-lg font-semibold text-gray-900">Status</p>
                    <Toggle label={group.isActive ? "Active" : "Inactive"} isSelected={group.isActive} onChange={() => undefined} size="md" isDisabled />
                </div>
                <p className="text-sm text-gray-500">
                    {group.isActive ? "This group is currently active and visible to all users." : "This group is inactive and hidden from users."}
                </p>
            </div>

            <hr className="border-gray-200" />

            {/* Group name */}
            <div>
                <Input label="Group name" value={groupName} onChange={onGroupNameChange} placeholder="Enter group name" isDisabled={isSaving} />
            </div>

            {/* Group members */}
            <div>
                <div className="mb-3 flex items-center justify-between">
                    <p className="text-lg font-semibold text-gray-900">Group members</p>
                    {membersTotalCount > 0 && (
                        <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            {membersTotalCount} members
                        </span>
                    )}
                </div>

                {/* Search and add members — excludes members beyond the 5-row preview too, not just what's visible */}
                <div className="mb-3">
                    <MemberSearchDropdown selectedUserIds={[...existingMemberIds, ...selectedNewMembers.map((m) => m.userId)]} onSelect={onNewMemberSelect} />
                </div>

                {selectedNewMembers.length > 0 && (
                    <div className="mb-3">
                        <p className="mb-1.5 text-sm font-medium text-gray-700">New members to add</p>
                        <div className="flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                            {selectedNewMembers.map((m) => (
                                <BatchUserPill key={m.userId} user={m} onRemove={onNewMemberRemove} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Preview table (first 5 members) — hidden in create mode since the group has no members yet */}
                {!isCreateMode && (
                    <GroupMembersTable
                        users={membersPreview}
                        isLoading={membersPreviewLoading}
                        groupId={groupId}
                        onRemoved={onMemberRemoved}
                        removeGroupMember={removeGroupMember}
                        pendingIds={removePendingIds}
                    />
                )}

                {!isCreateMode && membersTotalCount > membersPreview.length && (
                    <div className="mt-2 flex justify-end">
                        <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                            onClick={onShowAllMembers}
                        >
                            See all {membersTotalCount} members
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14m0 0-6-6m6 6-6 6" />
                            </svg>
                        </button>
                    </div>
                )}
            </div>

            {/* Set approvers */}
            <div>
                <p className="mb-3 text-lg font-semibold text-gray-900">Set approvers</p>

                <RadioGroup value={approverOption} onChange={(val) => onApproverOptionChange(val as GroupApproverMode)} className="flex flex-col gap-3">
                    {/* Option 1 — Set manager as approver */}
                    <div>
                        <RadioButton value={GroupApproverMode.Manager} label="Set manager as approver" />

                        {approverOption === GroupApproverMode.Manager && (
                            <div className="mt-3 ml-6 flex flex-col gap-3">
                                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                                    If you would like to add a back-up approver in case &ldquo;Manager&rdquo; is missing in user profile, please add email to
                                    the field below. However, populating the field below is not required.
                                </div>
                                <div>
                                    {/* Backup approvers are now bare IDs (no email hydration) — field stays locked/disabled until member add/search is supported */}
                                    <Input label="Backup approver email" defaultValue="" placeholder="d.millard@fastcompany.com" locked />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Option 2 — Set specific employee(s) as approver(s) */}
                    <div>
                        <div className="flex items-center gap-1.5">
                            <RadioButton value={GroupApproverMode.List} label="Set specific employee(s) as approver(s)" />
                            <div className="group relative flex items-center">
                                <svg
                                    className="h-4 w-4 cursor-help text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    aria-hidden="true"
                                >
                                    <circle cx="12" cy="12" r="10" />
                                    <path strokeLinecap="round" d="M12 16v-4M12 8h.01" />
                                </svg>
                                <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-72 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                                    Only users with the Approver role assigned in the Permissions page are available for selection.
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                </div>
                            </div>
                        </div>

                        {approverOption === GroupApproverMode.List && (
                            <div className="mt-3 ml-6 flex flex-col gap-2">
                                {isLoadingApproversBatch ? (
                                    <div className="flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                                        {Array.from({ length: 3 }).map((_, i) => (
                                            <UserPillSkeleton key={i} />
                                        ))}
                                    </div>
                                ) : selectedApprovers.length > 0 || approversBatchHasMore ? (
                                    <div className="flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                                        {selectedApprovers.map((a) => (
                                            <BatchUserPill key={a.userId} user={a} onRemove={onApproverRemove} />
                                        ))}
                                        {approversBatchHasMore && (
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                                onClick={onLoadMoreApprovers}
                                                disabled={isLoadingMoreApprovers}
                                            >
                                                {isLoadingMoreApprovers ? "Loading…" : "Load more"}
                                            </button>
                                        )}
                                    </div>
                                ) : null}
                                <ApproverSearchDropdown
                                    roleId={approverRoleId}
                                    isRoleLoading={isLoadingRoleIds}
                                    selectedUserIds={selectedApproverIds}
                                    onSelect={onApproverSelect}
                                />
                                {selectedApproverIds.length === 0 && !isLoadingApproversBatch && (
                                    <p className="text-sm text-red-600" role="alert">
                                        At least one approver is required when using this mode.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Option 3 — No approval required before accounting review (disabled) */}
                    <RadioButton value={GroupApproverMode.None} label="No approval required before accounting review" isDisabled />
                </RadioGroup>
            </div>

            {/* Set accounting approvers */}
            <div>
                <p className="mb-1 text-lg font-semibold text-gray-900">Set accounting approvers</p>
                <p className="mb-3 text-sm text-gray-500">
                    Select accounting approvers for this group. Only users with the Group Accounting Approver role assigned in the Permissions page are
                    available for selection.
                </p>
                {isLoadingAccountantsBatch ? (
                    <div className="mb-3 flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <UserPillSkeleton key={i} />
                        ))}
                    </div>
                ) : selectedAccountants.length > 0 || accountantsBatchHasMore ? (
                    <div className="mb-3 flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                        {selectedAccountants.map((a) => (
                            <BatchUserPill key={a.userId} user={a} onRemove={onAccountantRemove} />
                        ))}
                        {accountantsBatchHasMore && (
                            <button
                                type="button"
                                className="inline-flex items-center rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={onLoadMoreAccountants}
                                disabled={isLoadingMoreAccountants}
                            >
                                {isLoadingMoreAccountants ? "Loading…" : "Load more"}
                            </button>
                        )}
                    </div>
                ) : null}
                <ApproverSearchDropdown
                    roleId={accountantRoleId}
                    isRoleLoading={isLoadingRoleIds}
                    selectedUserIds={selectedAccountantIds}
                    onSelect={onAccountantSelect}
                />
            </div>

            {/* Policy exceptions */}
            <div>
                <p className="mb-0.5 text-lg font-semibold text-gray-900">Policy exceptions</p>
                <p className="mb-2 text-sm text-gray-500">Add policy exceptions that apply to this group</p>
                <PolicySearchDropdown
                    availablePolicies={availablePolicies}
                    selectedIds={selectedPolicyIds}
                    onSelect={(policyId) => onPolicyIdsChange([...selectedPolicyIds, policyId])}
                    isLoading={policiesLoading}
                />
                {policiesError ? (
                    <p role="alert" className="mt-2 text-sm text-red-600">
                        Failed to load policy exceptions.
                    </p>
                ) : policiesLoading ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {selectedPolicyIds.map((id) => (
                            <span key={id} className="inline-flex h-7 w-36 animate-pulse rounded-full border border-gray-100 bg-gray-100" />
                        ))}
                    </div>
                ) : selectedPolicyIds.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-400">No exceptions selected</p>
                ) : (
                    <div className="mt-2 flex flex-wrap gap-2 rounded-lg bg-gray-50 p-3">
                        {selectedPolicyIds.map((id) => {
                            const policy = availablePolicies.find((item) => item.policyId === id);
                            const status = policy?.status ?? "unknown";
                            const isInactive = status === "inactive";
                            const isDeleted = status === "deleted";
                            const isUnknown = status === "unknown";
                            const policyName = policy?.name ?? "Policy not found";
                            const title = isUnknown ? `Policy not found: ${id}` : isInactive ? "Policy Inactive" : isDeleted ? "Policy Deleted" : policyName;
                            return (
                                <span
                                    key={id}
                                    title={title}
                                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${
                                        isDeleted
                                            ? "border-red-200 bg-red-50 text-red-700 opacity-50"
                                            : isInactive || isUnknown
                                              ? `${isInactive ? "border-amber-200 bg-amber-50 text-amber-700 opacity-80" : "border-amber-200 bg-amber-50 text-amber-700"}`
                                              : "border-gray-200 bg-white text-gray-700"
                                    }`}
                                >
                                    {(isInactive || isUnknown) && (
                                        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path
                                                strokeLinecap="round"
                                                d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                                            />
                                        </svg>
                                    )}
                                    {isDeleted && (
                                        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <circle cx="12" cy="12" r="9" />
                                            <path strokeLinecap="round" d="M7 12h10" />
                                        </svg>
                                    )}
                                    <span className="max-w-[180px] truncate">{policyName}</span>
                                    <button
                                        type="button"
                                        aria-label={policy ? `Remove ${policy.name} policy` : `Remove unresolved policy ${id}`}
                                        className={`flex items-center justify-center ${isDeleted ? "text-red-500 hover:text-red-700" : isInactive || isUnknown ? "text-amber-500 hover:text-amber-700" : "text-gray-400 hover:text-gray-600"}`}
                                        onClick={() => onPolicyIdsChange(selectedPolicyIds.filter((sid) => sid !== id))}
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Receipt policy */}
            <div>
                <p className="mb-0.5 text-lg font-semibold text-gray-900">Receipt Policy</p>
                <p className="mb-2 text-sm text-gray-500">Select the receipt policy.</p>
                <Select
                    aria-label="Receipt policy"
                    selectedKey={selectedReceiptPolicyId ?? RECEIPT_POLICY_NONE_KEY}
                    onSelectionChange={(key) => onReceiptPolicyChange(!key || String(key) === RECEIPT_POLICY_NONE_KEY ? null : String(key))}
                    isDisabled={receiptPoliciesLoading || receiptPoliciesError !== null}
                >
                    <Select.Item id={RECEIPT_POLICY_NONE_KEY}>None</Select.Item>
                    {receiptPolicies
                        .filter((policy) => policy.status === "active" || policy.policyId === selectedReceiptPolicyId)
                        .map((p) => (
                            <Select.Item key={p.policyId} id={p.policyId} isDisabled={p.status !== "active"}>
                                {p.name}
                            </Select.Item>
                        ))}
                </Select>
                {receiptPoliciesError && (
                    <p role="alert" className="mt-2 text-sm text-red-600">
                        Failed to load receipt policies.
                    </p>
                )}
                {selectedReceiptPolicy && (
                    <div
                        className={`mt-3 rounded-lg border px-4 py-3 text-sm ${
                            selectedReceiptPolicyStatus === "deleted"
                                ? "border-red-200 bg-red-50 text-red-700"
                                : selectedReceiptPolicyStatus === "inactive"
                                  ? "border-amber-200 bg-amber-50 text-amber-700"
                                  : "border-gray-200 bg-gray-50/60 text-gray-600"
                        }`}
                        title={selectedReceiptPolicyTitle}
                    >
                        <p>
                            <span
                                className={`font-semibold ${selectedReceiptPolicyStatus === "deleted" ? "text-red-800" : selectedReceiptPolicyStatus === "inactive" ? "text-amber-800" : "text-gray-800"}`}
                            >
                                Policy:
                            </span>{" "}
                            {selectedReceiptPolicy.name}
                        </p>
                        <p className="mt-1">
                            <span className="font-semibold text-gray-800">Minimum Spend Amount:</span>{" "}
                            {parseReceiptRequiredAmount(selectedReceiptPolicy.rules ?? "") || "—"}
                        </p>
                    </div>
                )}
            </div>

            {/* Notify beyond approval workflow */}
            <div>
                <p className="mb-1 text-lg font-semibold text-gray-900">Notify beyond approval workflow</p>
                <p className="mb-3 text-sm text-gray-500">
                    The field below sends a notification alert email for all report submissions, clarification question requests, and answers to clarification
                    requests. This option may allow a Director or VP of a department, for example, to become aware of requests and questions without being a
                    formal approver in the approval workflow.
                </p>
                <Input
                    label='Company email (separate with a comma (",") if more than one)'
                    defaultValue={group.notifyEmail ?? ""}
                    placeholder="jbilliard@fastcompany.com"
                    locked
                />
            </div>

            {/* Custom data collection */}
            <div>
                <p className="mb-0.5 text-lg font-semibold text-gray-900">Custom data collection</p>
                <p className="mb-2 text-sm text-gray-500">Select custom fields required for this group&rsquo;s expense reports</p>
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2.5">
                    <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <circle cx="11" cy="11" r="8" />
                        <path strokeLinecap="round" d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search reporting fields..."
                        disabled
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
                        title="Custom field search not yet supported"
                    />
                </div>
                {group.customFields.length === 0 ? (
                    <p className="mt-2 text-sm text-gray-400">No custom fields selected</p>
                ) : (
                    <ul className="mt-2 flex flex-col gap-1">
                        {group.customFields.map((cf) => (
                            <li key={cf.id} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                {cf.label ?? cf.id}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function GroupDetailPanel({
    groupId,
    mode = "update",
    onClose,
    triggerRef,
    onRefreshList: onRefreshList,
    canWrite,
}: {
    groupId: string | null;
    mode?: "create" | "update";
    onClose: () => void;
    triggerRef: RefObject<HTMLElement | null>;
    onRefreshList: () => void;
    canWrite: boolean;
}) {
    const isCreateMode = mode === "create";
    const MEMBERS_PREVIEW_LIMIT = 5;
    // Fetched at the users-list API's max page size so the "add member" search can exclude
    // everyone already in the group, not just the 5 shown in the preview table (groups with
    // more members than this still have a — narrow, pre-existing-behavior — gap).
    const MEMBERS_FETCH_LIMIT = 100;
    const BATCH_LIMIT = 10;
    const DRAWER_MAX_WIDTH = "520px";

    const { group, isLoading, error, reload } = useGroupDetail(isCreateMode ? null : groupId);
    const { updateGroup, isLoading: isSaving, clearError } = useUpdateGroup();
    const { createGroup, isLoading: isCreating } = useCreateGroup();
    const { showError, showSuccess, showWarning } = useNotifications();
    const { policies: availablePolicies, isLoading: policiesLoading, error: policiesError } = useGetPoliciesForGroup();
    const { policies: receiptPolicies, isLoading: receiptPoliciesLoading, error: receiptPoliciesError } = useGetPoliciesForGroup("receipt");

    // Fires as soon as groupId is set; only the first 5 are shown in the preview table below.
    const {
        users: fetchedMembers,
        pageInfo: membersPageInfo,
        isLoading: membersPreviewLoading,
        reload: reloadMembersPreview,
    } = useGroupMembers(isCreateMode ? null : groupId, MEMBERS_FETCH_LIMIT);
    const membersPreview = fetchedMembers.slice(0, MEMBERS_PREVIEW_LIMIT);
    const existingMemberIds = fetchedMembers.map((m) => m.userId);

    // Batch-hydrate approvers and accountants — IDs come from getGroupById result
    const approverIds = group?.approverConfig?.approvers ?? [];
    const accountantIds = group?.accountants ?? [];

    const {
        users: hydratedApprovers,
        isLoading: isLoadingApproversBatch,
        isLoadingMore: isLoadingMoreApprovers,
        hasMore: approversBatchHasMore,
        loadMore: loadMoreApprovers,
    } = useGroupsUsersByBatch(approverIds, BATCH_LIMIT);

    const {
        users: hydratedAccountants,
        isLoading: isLoadingAccountantsBatch,
        isLoadingMore: isLoadingMoreAccountants,
        hasMore: accountantsBatchHasMore,
        loadMore: loadMoreAccountants,
    } = useGroupsUsersByBatch(accountantIds, BATCH_LIMIT);

    const { removeGroupMember, pendingIds: removePendingIds } = useRemoveGroupMember();
    const { assignGroupMembers, isLoading: isAssigningMembers } = useAssignGroupMembers();

    // ── Form state ────────────────────────────────────────────────────────────
    const [view, setView] = useState<"detail" | "members">("detail");
    const [groupName, setGroupName] = useState("");
    // Create mode has no data to fetch/hydrate, so the form is ready immediately.
    const [isGroupNameReady, setIsGroupNameReady] = useState(isCreateMode);
    const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
    const [initialExceptionIds, setInitialExceptionIds] = useState<string[]>([]);
    const [selectedReceiptPolicyId, setSelectedReceiptPolicyId] = useState<string | null>(null);
    const [initialReceiptPolicyId, setInitialReceiptPolicyId] = useState<string | null>(null);
    // Mandatory field — default to "manager" in create mode so there's nothing to validate.
    const [approverOption, setApproverOption] = useState<GroupApproverMode | "">(isCreateMode ? GroupApproverMode.Manager : "");
    // The canonical selected id sets — seeded synchronously from the group (no batch fetch needed),
    // so change-detection/save are correct even before a single page of pill data has loaded.
    const [selectedApproverIds, setSelectedApproverIds] = useState<string[]>([]);
    const [selectedAccountantIds, setSelectedAccountantIds] = useState<string[]>([]);
    // Display-only pill data — filled in page by page as batch data loads (initial page or
    // manual "Load more"); never used for change-detection or the save payload.
    const [selectedApprovers, setSelectedApprovers] = useState<GroupApproverUser[]>([]);
    const [selectedAccountants, setSelectedAccountants] = useState<GroupApproverUser[]>([]);
    // Members staged for adding — fully-hydrated at select time (search results already have
    // name/email), so unlike approvers/accountants there's no separate id-array + batch-hydration.
    const [selectedNewMembers, setSelectedNewMembers] = useState<GroupApproverUser[]>([]);
    const [showConcurrencyModal, setShowConcurrencyModal] = useState(false);
    const concurrencyTitleId = useId();
    const hydratedGroupIdRef = useRef<string | null>(null);
    const receiptPolicyIdSet = useMemo(() => new Set(receiptPolicies.map((p) => p.policyId)), [receiptPolicies]);

    // Hydrate controlled fields from the group (policy split + approver mode + selected id sets)
    useEffect(() => {
        // Create mode starts with an empty form — nothing to hydrate from the (non-existent) group.
        if (isCreateMode) return;
        if (!group) {
            hydratedGroupIdRef.current = null;
            setIsGroupNameReady(false);
            return;
        }
        if (receiptPoliciesLoading || receiptPoliciesError) return;
        if (hydratedGroupIdRef.current === group.groupId) return;
        hydratedGroupIdRef.current = group.groupId;

        const groupPolicyIds = group.policyIds ?? [];
        const receiptId = groupPolicyIds.find((id) => receiptPolicyIdSet.has(id)) ?? null;
        const exceptionIds = groupPolicyIds.filter((id) => !receiptPolicyIdSet.has(id));

        setGroupName(group.groupName ?? "");
        setSelectedPolicyIds(exceptionIds);
        setInitialExceptionIds(exceptionIds);
        setSelectedReceiptPolicyId(receiptId);
        setInitialReceiptPolicyId(receiptId);
        setApproverOption(
            (() => {
                const raw = group.approverConfig?.approverMode as string | null | undefined;
                if (!raw) return "";
                if (raw === GroupApproverMode.Alternate) return GroupApproverMode.List;
                return raw as GroupApproverMode;
            })(),
        );
        setSelectedApproverIds(approverIds);
        setSelectedAccountantIds(accountantIds);
        setSelectedApprovers([]);
        setSelectedAccountants([]);
        setSelectedNewMembers([]);
        setIsGroupNameReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group, receiptPoliciesLoading, receiptPolicyIdSet]);

    // Merge each newly-loaded page of hydrated approvers into the pill list, keeping only ids that
    // are still selected (a removal that happens while a page is in flight must stick).
    useEffect(() => {
        if (hydratedApprovers.length === 0) return;
        setSelectedApprovers((prev) => {
            const existingIds = new Set(prev.map((a) => a.userId));
            const toAdd = hydratedApprovers.filter((u) => selectedApproverIds.includes(u.userId) && !existingIds.has(u.userId));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
    }, [hydratedApprovers, selectedApproverIds]);

    // Merge each newly-loaded page of hydrated accountants into the pill list (see approvers above).
    useEffect(() => {
        if (hydratedAccountants.length === 0) return;
        setSelectedAccountants((prev) => {
            const existingIds = new Set(prev.map((a) => a.userId));
            const toAdd = hydratedAccountants.filter((u) => selectedAccountantIds.includes(u.userId) && !existingIds.has(u.userId));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
    }, [hydratedAccountants, selectedAccountantIds]);

    const handleClose = useCallback(() => {
        onClose();
        triggerRef.current?.focus();
    }, [onClose, triggerRef]);

    const handleMemberRemoved = useCallback(() => {
        reloadMembersPreview();
    }, [reloadMembersPreview]);

    // ── Derived change detection ──────────────────────────────────────────────
    const trimmedGroupName = groupName.trim();
    const originalTrimmedName = group?.groupName?.trim() ?? "";
    const isGroupNameValid = trimmedGroupName.length > 0;
    const hasGroupNameChanged = trimmedGroupName !== originalTrimmedName;

    const originalPolicyIds = initialExceptionIds;
    const hasPolicyIdsChanged = selectedPolicyIds.length !== originalPolicyIds.length || selectedPolicyIds.some((id) => !originalPolicyIds.includes(id));
    const hasReceiptPolicyChanged = selectedReceiptPolicyId !== initialReceiptPolicyId;

    const originalApproverIds = approverIds.slice().sort().join(",");
    const currentApproverIds = selectedApproverIds.slice().sort().join(",");
    const normalizedOriginalApproverMode = (() => {
        const raw = group?.approverConfig?.approverMode as string | null | undefined;
        if (!raw) return "";
        if (raw === GroupApproverMode.Alternate) return GroupApproverMode.List;
        return raw as GroupApproverMode;
    })();
    const hasApproverChanged = approverOption !== normalizedOriginalApproverMode || currentApproverIds !== originalApproverIds;

    const originalAccountantIds = accountantIds.slice().sort().join(",");
    const currentAccountantIds = selectedAccountantIds.slice().sort().join(",");
    const hasAccountantsChanged = currentAccountantIds !== originalAccountantIds;

    const hasAnyChange =
        hasGroupNameChanged || hasPolicyIdsChanged || hasReceiptPolicyChanged || hasApproverChanged || hasAccountantsChanged || selectedNewMembers.length > 0;

    // Keep the skeleton up until at least the first page of pills has been merged in — selectedApproverIds
    // is already known synchronously from the group, so this never depends on hydration "finishing".
    // Skeleton reflects server hydration progress only — never the mutable selection arrays, so
    // manually removing pills (down to zero) doesn't re-trigger the skeleton or hide "Load more".
    const showApproversSkeleton = isLoadingApproversBatch || (approverIds.length > 0 && hydratedApprovers.length === 0);
    const showAccountantsSkeleton = isLoadingAccountantsBatch || (accountantIds.length > 0 && hydratedAccountants.length === 0);

    // ── Members full view ─────────────────────────────────────────────────────
    if (view === "members") {
        return (
            <>
                <GroupMembersFullView
                    groupId={groupId ?? ""}
                    totalMembersCount={membersPageInfo?.totalCount ?? 0}
                    onBack={() => setView("detail")}
                    onClose={handleClose}
                    maxWidth={DRAWER_MAX_WIDTH}
                />
                {showConcurrencyModal && (
                    <ModalOverlay
                        isOpen={showConcurrencyModal}
                        onOpenChange={(open) => {
                            if (!open) setShowConcurrencyModal(false);
                        }}
                        isDismissable={false}
                        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
                    >
                        <Modal className="max-w-sm">
                            <Dialog aria-labelledby={concurrencyTitleId} className="outline-none">
                                <div className="w-full rounded-xl bg-white p-6 shadow-xl">
                                    <h2 id={concurrencyTitleId} className="text-lg font-semibold text-gray-900">
                                        Group Updated by Another User
                                    </h2>
                                    <p className="mt-2 text-sm text-gray-600">This group was modified by someone else while you had it open.</p>
                                    <div className="mt-6 flex justify-end">
                                        <Button color="primary" size="md" onPress={() => setShowConcurrencyModal(false)}>
                                            OK
                                        </Button>
                                    </div>
                                </div>
                            </Dialog>
                        </Modal>
                    </ModalOverlay>
                )}
            </>
        );
    }

    return (
        <>
            <Drawer
                title={isCreateMode ? "Add Group" : "Update Group"}
                onClose={handleClose}
                maxWidth={DRAWER_MAX_WIDTH}
                isBusy={showConcurrencyModal}
                isLoading={!isCreateMode && (isLoading || (!isGroupNameReady && !receiptPoliciesError))}
                loadingLabel="Loading group details"
                loadingMessage="Loading group details…"
                bodyClassName="p-0"
                footer={
                    <Button
                        color="primary"
                        size="md"
                        className="w-full"
                        isDisabled={
                            isCreateMode
                                ? !canWrite ||
                                  isCreating ||
                                  isAssigningMembers ||
                                  !isGroupNameValid ||
                                  (approverOption === GroupApproverMode.List && selectedApproverIds.length === 0)
                                : !canWrite ||
                                  !group ||
                                  !group.groupEpoch ||
                                  !isGroupNameReady ||
                                  isSaving ||
                                  isAssigningMembers ||
                                  isLoading ||
                                  !isGroupNameValid ||
                                  !hasAnyChange ||
                                  (approverOption === GroupApproverMode.List && selectedApproverIds.length === 0)
                        }
                        isLoading={isCreateMode ? isCreating || isAssigningMembers : isSaving || isAssigningMembers}
                        onPress={() => {
                            const mergedPolicyIds = [...selectedPolicyIds, ...(selectedReceiptPolicyId ? [selectedReceiptPolicyId] : [])];
                            const newMemberIds = selectedNewMembers.map((m) => m.userId);

                            if (isCreateMode) {
                                void createGroup(
                                    trimmedGroupName,
                                    mergedPolicyIds,
                                    {
                                        approverMode: approverOption as GroupApproverMode,
                                        approvers: approverOption === GroupApproverMode.List ? selectedApproverIds : [],
                                    },
                                    selectedAccountantIds,
                                )
                                    .then((newGroup) => {
                                        if (newMemberIds.length === 0) {
                                            onRefreshList();
                                            handleClose();
                                            return;
                                        }
                                        return assignGroupMembers(newGroup.groupId, newMemberIds)
                                            .then(({ succeededUserIds, failed }) => {
                                                onRefreshList();
                                                if (failed.length === 0) {
                                                    showSuccess("Group created", `${succeededUserIds.length} member(s) added.`);
                                                } else {
                                                    showWarning(
                                                        "Group created, but some members could not be added",
                                                        `${succeededUserIds.length} added, ${failed.length} failed.`,
                                                    );
                                                }
                                                handleClose();
                                            })
                                            .catch(() => {
                                                onRefreshList();
                                                showError("Group created, but adding members failed", "Please add the members again from the group.");
                                                handleClose();
                                            });
                                    })
                                    .catch(() => {
                                        showError("Failed to create group", "Something went wrong. Please try again.");
                                    });
                                return;
                            }

                            if (!group || !group.groupEpoch) return;
                            const valueToSave = trimmedGroupName;
                            void updateGroup(
                                group.groupId,
                                valueToSave,
                                group.groupEpoch,
                                mergedPolicyIds,
                                {
                                    approverMode: approverOption as GroupApproverMode,
                                    approvers: approverOption === GroupApproverMode.List ? selectedApproverIds : [],
                                },
                                selectedAccountantIds,
                            )
                                .then(() => {
                                    if (newMemberIds.length === 0) {
                                        onRefreshList();
                                        handleClose();
                                        return;
                                    }
                                    return assignGroupMembers(group.groupId, newMemberIds)
                                        .then(({ succeededUserIds, failed }) => {
                                            reloadMembersPreview();
                                            onRefreshList();
                                            if (failed.length === 0) {
                                                showSuccess("Group updated", `${succeededUserIds.length} member(s) added.`);
                                                handleClose();
                                            } else {
                                                setSelectedNewMembers((prev) => prev.filter((m) => failed.some((f) => f.userId === m.userId)));
                                                showWarning(
                                                    "Some members could not be added",
                                                    `${succeededUserIds.length} added, ${failed.length} failed. Failed members are still listed below.`,
                                                );
                                            }
                                        })
                                        .catch(() => {
                                            onRefreshList();
                                            showError("Group updated, but adding members failed", "Please try adding the members again.");
                                        });
                                })
                                .catch((err) => {
                                    if (err instanceof GroupConcurrencyError) {
                                        clearError();
                                        setShowConcurrencyModal(true);
                                    } else {
                                        showError("Failed to update group", "Something went wrong. Please try again.");
                                    }
                                });
                        }}
                    >
                        {isCreateMode ? "Create Group" : "Update Group"}
                    </Button>
                }
            >
                {error && !isLoading && (
                    <div className="flex h-full items-center justify-center px-6">
                        <p className="text-center text-sm text-red-600">{error}</p>
                    </div>
                )}

                {(isCreateMode || (group && !isLoading && (isGroupNameReady || receiptPoliciesError))) && (
                    <GroupDetailContent
                        group={isCreateMode ? EMPTY_GROUP_FOR_CREATE : (group as GroupDetailItem)}
                        groupId={groupId ?? ""}
                        groupName={groupName}
                        onGroupNameChange={setGroupName}
                        isSaving={isCreateMode ? isCreating : isSaving}
                        isCreateMode={isCreateMode}
                        selectedPolicyIds={selectedPolicyIds}
                        onPolicyIdsChange={setSelectedPolicyIds}
                        availablePolicies={availablePolicies}
                        policiesLoading={policiesLoading}
                        policiesError={policiesError}
                        receiptPolicies={receiptPolicies}
                        receiptPoliciesLoading={receiptPoliciesLoading}
                        receiptPoliciesError={receiptPoliciesError}
                        selectedReceiptPolicyId={selectedReceiptPolicyId}
                        onReceiptPolicyChange={setSelectedReceiptPolicyId}
                        approverOption={approverOption}
                        onApproverOptionChange={(val) => setApproverOption(val)}
                        selectedApprovers={selectedApprovers}
                        selectedApproverIds={selectedApproverIds}
                        onApproverSelect={(u) => {
                            setSelectedApproverIds((prev) => (prev.includes(u.userId) ? prev : [...prev, u.userId]));
                            setSelectedApprovers((prev) =>
                                prev.some((a) => a.userId === u.userId) ? prev : [...prev, { userId: u.userId, emailId: u.emailId, legalName: u.legalName }],
                            );
                        }}
                        onApproverRemove={(userId) => {
                            setSelectedApproverIds((prev) => prev.filter((id) => id !== userId));
                            setSelectedApprovers((prev) => prev.filter((a) => a.userId !== userId));
                        }}
                        isLoadingApproversBatch={showApproversSkeleton}
                        isLoadingMoreApprovers={isLoadingMoreApprovers}
                        approversBatchHasMore={approversBatchHasMore}
                        onLoadMoreApprovers={loadMoreApprovers}
                        selectedAccountants={selectedAccountants}
                        selectedAccountantIds={selectedAccountantIds}
                        onAccountantSelect={(u) => {
                            setSelectedAccountantIds((prev) => (prev.includes(u.userId) ? prev : [...prev, u.userId]));
                            setSelectedAccountants((prev) =>
                                prev.some((a) => a.userId === u.userId) ? prev : [...prev, { userId: u.userId, emailId: u.emailId, legalName: u.legalName }],
                            );
                        }}
                        onAccountantRemove={(userId) => {
                            setSelectedAccountantIds((prev) => prev.filter((id) => id !== userId));
                            setSelectedAccountants((prev) => prev.filter((a) => a.userId !== userId));
                        }}
                        isLoadingAccountantsBatch={showAccountantsSkeleton}
                        isLoadingMoreAccountants={isLoadingMoreAccountants}
                        accountantsBatchHasMore={accountantsBatchHasMore}
                        onLoadMoreAccountants={loadMoreAccountants}
                        onShowAllMembers={() => setView("members")}
                        membersPreview={membersPreview}
                        membersPreviewLoading={membersPreviewLoading}
                        membersTotalCount={membersPageInfo?.totalCount ?? 0}
                        removeGroupMember={removeGroupMember}
                        removePendingIds={removePendingIds}
                        onMemberRemoved={handleMemberRemoved}
                        existingMemberIds={existingMemberIds}
                        selectedNewMembers={selectedNewMembers}
                        onNewMemberSelect={(u) => {
                            setSelectedNewMembers((prev) =>
                                prev.some((m) => m.userId === u.userId) ? prev : [...prev, { userId: u.userId, emailId: u.emailId, legalName: u.legalName }],
                            );
                        }}
                        onNewMemberRemove={(userId) => {
                            setSelectedNewMembers((prev) => prev.filter((m) => m.userId !== userId));
                        }}
                    />
                )}
            </Drawer>

            {/* Concurrency error modal */}
            <ModalOverlay
                isOpen={showConcurrencyModal}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowConcurrencyModal(false);
                        reload();
                        onRefreshList();
                    }
                }}
                isDismissable={false}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
            >
                <Modal className="max-w-sm">
                    <Dialog aria-labelledby={concurrencyTitleId} className="outline-none">
                        <div className="w-full rounded-xl bg-white p-6 shadow-xl">
                            <h2 id={concurrencyTitleId} className="text-lg font-semibold text-gray-900">
                                Group Updated by Another User
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                This group was modified by someone else while you had it open. The latest version will be reloaded so you can apply your
                                changes.
                            </p>
                            <div className="mt-6 flex justify-end">
                                <Button color="primary" size="md" onPress={() => setShowConcurrencyModal(false)}>
                                    OK
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </>
    );
}
// ─── Groups Page ──────────────────────────────────────────────────────────────

export function GroupsPage() {
    const canWrite = useCan(ACTIONS.groups.write);
    const [filter, setFilter] = useState<GroupsQueryFilters>({});
    const { groupItems, pageInfo, currentPage, isLoading, error, setPage, refresh } = useGroupsList(filter);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [isAddingGroup, setIsAddingGroup] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);

    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * pageInfo.limit,
              limit: pageInfo.limit,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    return (
        <div className="flex flex-col gap-6">
            {/* Page heading + actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Groups</h1>
                    <p className="mt-2 text-sm text-gray-600">Manage user groups and assignments.</p>
                </div>

                <div className="flex shrink-0 items-center gap-3">
                    <Button color="secondary" size="md" isDisabled title="Export not yet supported" iconLeading={IconDownload}>
                        Export
                    </Button>
                    <Button
                        color="primary"
                        size="md"
                        iconLeading={IconGroupPlus}
                        isDisabled={!canWrite}
                        onPress={(e) => {
                            triggerRef.current = (e.target as HTMLElement) ?? null;
                            setIsAddingGroup(true);
                        }}
                    >
                        Add Group
                    </Button>
                </div>
            </div>

            {/* Error */}
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            {/* Table */}
            <GroupsTable
                groups={groupItems}
                isLoading={isLoading}
                pageInfo={tablePageInfo}
                onPageChange={setPage}
                onSelect={(groupId) => {
                    triggerRef.current = document.querySelector<HTMLElement>(`[data-group-id="${groupId}"]`);
                    setSelectedGroupId(groupId);
                }}
                filter={filter}
                onFilter={setFilter}
            />

            {/* Group detail drawer */}
            {(selectedGroupId || isAddingGroup) && (
                <GroupDetailPanel
                    groupId={selectedGroupId}
                    mode={isAddingGroup ? "create" : "update"}
                    onClose={() => {
                        setSelectedGroupId(null);
                        setIsAddingGroup(false);
                    }}
                    triggerRef={triggerRef}
                    onRefreshList={refresh}
                    canWrite={canWrite}
                />
            )}
        </div>
    );
}
